import { useEffect, useState, useCallback, useRef } from 'react';

import isNil from 'lodash-es/isNil';
import { useDispatch, useSelector } from 'react-redux';

import useDebounce from '@/hooks/useDebounce';
import { useDeskId } from '@/hooks/useDeskId';
import useMessages from '@/hooks/useMessages';
import {
  useConversationDetail,
  useConversationMutations,
} from '@/modules/humanChat/hooks/useConversations';
import { setChatInput } from '@/modules/humanChat/redux/actions';
import { selectChatInput } from '@/modules/humanChat/redux/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { getTextBeforeCaret, setCursorAtOffset } from '@/util/dom';
import { isKeyEnter } from '@/util/util';

import { getMessageType } from '../helpers';
import { CHANNELS_WITHOUT_LIVE_CHAT } from './constants';
import { useFileUpload } from './useFileUpload';
import {
  getQuickResponseQuery,
  stripQuickResponseToken,
  useQuickResponses,
} from './useQuickResponses';

export type { UploadedData } from './useFileUpload';

export const useChatBox = () => {
  const sessionId = useSelector(selectSessionId);
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const chatInput = useSelector((state: RootState) => selectChatInput(state, sessionId));

  // Conversation and messaging
  const { conversation, getStatus } = useConversationDetail({ deskId, sessionId });
  const { updateTyping, updateConversation } = useConversationMutations({ deskId, sessionId });
  const { createMessage, createStatus, createPresignedUrl } = useMessages(deskId, sessionId);

  // Text input and typing
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 1000);
  const inputRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<boolean>(false);

  // Single source of truth for the quick-responses menu: the active slash-command
  // query, or null when the caret is not inside a command token.
  const [quickResponseQuery, setQuickResponseQuery] = useState<string | null>(null);
  const showQuickResponses = quickResponseQuery !== null;

  const updateInputText = useCallback(
    (text = '') => {
      if (inputRef.current) {
        inputRef.current.innerText = text;
      }
      setInput(text);
      setQuickResponseQuery(null);
      dispatch(setChatInput({ sessionId, text }));
    },
    [dispatch, sessionId]
  );

  // Insert a saved response in place of the active "/" command token, keeping
  // the rest of the message intact (the trigger fires anywhere, so selecting
  // must not overwrite text typed before the command).
  const selectQuickResponse = useCallback(
    (text: string) => {
      const el = inputRef.current;
      if (!el) {
        return;
      }
      const beforeCaret = getTextBeforeCaret(el);
      const after = el.innerText.slice(beforeCaret.length);
      const before = stripQuickResponseToken(beforeCaret);
      updateInputText(`${before}${text}${after}`);
      // Place the caret right after the inserted response synchronously — deferring
      // it lets the browser paint the caret at the end first, causing a visible jump.
      setCursorAtOffset(el, before.length + text.length);
    },
    [updateInputText]
  );

  // Sub-hooks
  const quickResponses = useQuickResponses(quickResponseQuery);
  const { filteredOptions, selectedText } = quickResponses;

  const fileUpload = useFileUpload({ channel: conversation?.channel, createPresignedUrl });

  // Typing indicator
  useEffect(() => {
    if (!isNil(debouncedInput) && typingRef.current) {
      updateTyping('stop');
      typingRef.current = false;
    }
  }, [debouncedInput, updateTyping]);

  // Reset on conversation close
  useEffect(() => {
    if (conversation?.status === 'closed' && input !== '') {
      updateInputText('');
      fileUpload.setFiles([]);
    }
  }, [conversation?.status, input, updateInputText, fileUpload.setFiles]);

  // Reset input when switching conversations. Do NOT add chatInput or
  // updateInputText to deps — it creates a feedback loop that resets the
  // cursor on every keystroke (chatInput updates Redux on each input event).
  useEffect(() => {
    if (inputRef?.current) {
      updateInputText(chatInput || '');
      if (conversation?.status !== 'closed') {
        // Double rAF: wait two paints so the textarea is mounted and laid out after
        // the conversation switch before focusing — a single frame can land before
        // layout settles, and the browser ignores focus on a not-yet-rendered element.
        requestAnimationFrame(() => requestAnimationFrame(() => inputRef.current?.focus()));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef, sessionId]);

  // Submission
  const handleClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (fileUpload.uploading) {
        return;
      }

      const hasInput = input && input.trim() !== '';

      const attachments = fileUpload.uploadedData.map((data) => ({
        id: data.id,
        type: getMessageType({ mimeType: data.mime_type, channel: conversation?.channel }),
        title: data.original_name,
        filename: data.name,
        mime_type: data.mime_type,
      }));

      if (!hasInput && attachments.length === 0) {
        inputRef.current?.focus();
        return;
      }

      const text = hasInput ? input.trim() : undefined;
      createMessage({ body: { text, attachments } }, { onSuccess: () => updateInputText('') });

      fileUpload.clearUploads();

      if (!isNil(input)) {
        updateTyping('stop');
        typingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fileUpload.uploading,
      fileUpload.uploadedData,
      fileUpload.clearUploads,
      input,
      conversation?.channel,
      createMessage,
      updateInputText,
      updateTyping,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (isKeyEnter(e) && !e.shiftKey && !showQuickResponses) {
        e.preventDefault();
      }
    },
    [showQuickResponses]
  );

  const handleKeyPress = useCallback(
    async (event) => {
      if (isKeyEnter(event) && showQuickResponses) {
        event.preventDefault();
        if (selectedText) {
          selectQuickResponse(selectedText); // clears the query via updateInputText
        } else {
          setQuickResponseQuery(null);
        }
        return;
      }

      const canSendMessage =
        isKeyEnter(event) &&
        !event.shiftKey &&
        !showQuickResponses &&
        !(getStatus === 'pending' || fileUpload.uploading || createStatus === 'pending');

      if (canSendMessage) {
        event.preventDefault();
        handleClick(event);
        return;
      }

      if (showQuickResponses) {
        if (event.code === 'ArrowUp') {
          quickResponses.navigateUp();
        } else if (event.code === 'ArrowDown') {
          quickResponses.navigateDown();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showQuickResponses,
      getStatus,
      fileUpload.uploading,
      createStatus,
      selectQuickResponse,
      selectedText,
      handleClick,
      quickResponses.navigateUp,
      quickResponses.navigateDown,
    ]
  );

  const handleChange = useCallback(() => {
    if (conversation?.status === 'closed' && conversation?.state?.active) {
      updateConversation({ status: 'open' });
    }
    if (!typingRef.current) {
      updateTyping('start');
      typingRef.current = true;
    }

    const el = inputRef.current;
    if (!el) {
      return;
    }

    // Open the menu only when the caret sits inside a "/" command token.
    const query = getQuickResponseQuery(getTextBeforeCaret(el));
    setQuickResponseQuery(query);

    if (query !== null && /\n/.test(el.innerText)) {
      return;
    }

    const trimmedInput = el.innerText.trim();
    if (!trimmedInput && el.innerHTML !== '') {
      el.innerHTML = '';
    }
    setInput(trimmedInput);
    dispatch(setChatInput({ sessionId, text: el.innerText }));
  }, [
    conversation?.state?.active,
    conversation?.status,
    dispatch,
    sessionId,
    updateConversation,
    updateTyping,
  ]);

  const changeInputTo = useCallback(
    (text: string) => {
      updateInputText(text);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [updateInputText]
  );

  const handlePaste = useCallback(
    async (e) => {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain');
      if (text) {
        document.execCommand('insertText', false, text.trim());
        return;
      }
      await fileUpload.handlePasteImage();
    },
    [fileUpload.handlePasteImage] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Derived UI states
  const isChannelSupported = !CHANNELS_WITHOUT_LIVE_CHAT.includes(conversation?.channel);

  return {
    files: fileUpload.files,
    isClosed: conversation?.state?.active && conversation?.status === 'closed',
    isActive: conversation?.state?.active,
    isLoading: getStatus === 'pending' || fileUpload.uploading || createStatus === 'pending',
    isButtonDisabled: fileUpload.uploading,
    isUnavailable: (!conversation?.state?.active && getStatus !== 'pending') || !isChannelSupported,
    inputRef,
    filteredOptions,
    showQuickResponses,
    handleClick,
    handleChange,
    changeInputTo,
    selectQuickResponse,
    handleKeyPress,
    handleKeyDown,
    handleOnRemoveFile: fileUpload.handleOnRemoveFile,
    handlePaste,
    getRootProps: fileUpload.getRootProps,
    getInputProps: fileUpload.getInputProps,
    open: fileUpload.open,
    isDragActive: fileUpload.isDragActive,
    audioUrl: fileUpload.audioUrl,
    handleStartRecording: fileUpload.handleStartRecording,
    isRecording: fileUpload.isRecording,
    recordingTime: fileUpload.recordingTime,
    recordingBlob: fileUpload.recordingBlob,
    isTranscoding: fileUpload.isTranscoding,
    handleStopRecording: fileUpload.handleStopRecording,
    handleDeleteRecording: fileUpload.clearUploads,
    totalRecordingTime: fileUpload.totalRecordingTime,
    isChannelSupported,
    containerRef: fileUpload.containerRef,
    isPlaying: fileUpload.isPlaying,
    currentTime: fileUpload.currentTime,
    duration: fileUpload.duration,
    playPause: fileUpload.playPause,
  };
};

export type ChatBoxController = ReturnType<typeof useChatBox>;
